import React, { useState, useEffect } from 'react';
import {
  Box, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
  Badge, Flex, Select, Button, HStack, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Input, Textarea, useDisclosure, useToast,
  IconButton, Grid, GridItem, Stat, StatLabel, StatNumber,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { MdAdd, MdEdit, MdDelete, MdCalendarToday } from 'react-icons/md';
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent } from '../../services/academic.service';

const TYPE_MAP = {
  SEMESTER_START: { label: 'Inicio Semestre', color: 'green' },
  SEMESTER_END: { label: 'Fin Semestre', color: 'red' },
  ENROLLMENT: { label: 'Inscripciones', color: 'blue' },
  EXAM_PERIOD: { label: 'Exámenes', color: 'purple' },
  HOLIDAY: { label: 'Feriado', color: 'orange' },
  EVENT: { label: 'Evento', color: 'teal' },
  OTHER: { label: 'Otro', color: 'gray' },
};

export default function CalendarView() {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const cardBg = useColorModeValue('white', 'navy.800');
  const toast = useToast();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', eventType: 'EVENT', startDate: '', endDate: '', description: '' });

  useEffect(() => { load(); }, [year]);

  const load = async () => {
    try { setLoading(true); const r = await getCalendarEvents(year); setEvents(r?.payload?.data || []); } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title || !form.startDate) { toast({ title: 'Completá título y fecha', status: 'warning', duration: 3000 }); return; }
    try {
      if (editMode) { await updateCalendarEvent(editId, form); }
      else { await createCalendarEvent({ ...form, year, createdBy: 1 }); }
      toast({ title: editMode ? 'Evento actualizado' : 'Evento creado', status: 'success', duration: 3000 });
      onClose(); load();
    } catch (e) { toast({ title: 'Error', status: 'error', duration: 3000 }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar evento?')) return;
    try { await deleteCalendarEvent(id); toast({ title: 'Eliminado', status: 'success', duration: 2000 }); load(); } catch (e) { toast({ title: 'Error', status: 'error' }); }
  };

  const openCreate = () => { setEditMode(false); setEditId(null); setForm({ title: '', eventType: 'EVENT', startDate: '', endDate: '', description: '' }); onOpen(); };
  const openEdit = (ev) => { setEditMode(true); setEditId(ev.id); setForm({ title: ev.title, eventType: ev.event_type, startDate: ev.start_date?.split('T')[0], endDate: ev.end_date?.split('T')[0] || '', description: ev.description || '' }); onOpen(); };

  const typeCounts = Object.entries(TYPE_MAP).map(([k, v]) => ({ ...v, key: k, count: events.filter(e => e.event_type === k).length })).filter(t => t.count > 0);

  if (loading) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {typeCounts.length > 0 && (
        <Grid templateColumns={{ base: '1fr', md: `repeat(${Math.min(typeCounts.length, 4)}, 1fr)` }} gap={4} mb={6}>
          {typeCounts.slice(0, 4).map(t => (
            <GridItem key={t.key}><Card bg={cardBg} p={4}><Stat><StatLabel>{t.label}</StatLabel><StatNumber color={`${t.color}.500`}>{t.count}</StatNumber></Stat></Card></GridItem>
          ))}
        </Grid>
      )}
      <Card p={4}>
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          <HStack>
            <Select w="120px" value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
            <Text fontSize="sm" color="gray.500">{events.length} eventos</Text>
          </HStack>
          <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={openCreate}>Nuevo evento</Button>
        </Flex>
        <Box overflowX="auto"><Table size="sm" variant="simple"><Thead><Tr><Th borderColor={borderColor}>Tipo</Th><Th borderColor={borderColor}>Título</Th><Th borderColor={borderColor}>Fecha inicio</Th><Th borderColor={borderColor}>Fecha fin</Th><Th borderColor={borderColor}>Descripción</Th><Th borderColor={borderColor}>Acciones</Th></Tr></Thead><Tbody>
          {events.sort((a, b) => new Date(a.start_date) - new Date(b.start_date)).map(ev => {
            const t = TYPE_MAP[ev.event_type] || { label: ev.event_type, color: 'gray' };
            return (
              <Tr key={ev.id}>
                <Td borderColor={borderColor}><Badge colorScheme={t.color}>{t.label}</Badge></Td>
                <Td borderColor={borderColor}><Text fontWeight="600" fontSize="sm">{ev.title}</Text></Td>
                <Td borderColor={borderColor}>{new Date(ev.start_date).toLocaleDateString('es-UY')}</Td>
                <Td borderColor={borderColor}>{ev.end_date ? new Date(ev.end_date).toLocaleDateString('es-UY') : '-'}</Td>
                <Td borderColor={borderColor}><Text fontSize="sm" noOfLines={1}>{ev.description || '-'}</Text></Td>
                <Td borderColor={borderColor}><HStack spacing={1}>
                  <IconButton size="xs" icon={<MdEdit />} variant="ghost" onClick={() => openEdit(ev)} aria-label="Editar" />
                  <IconButton size="xs" icon={<MdDelete />} variant="ghost" colorScheme="red" onClick={() => handleDelete(ev.id)} aria-label="Eliminar" />
                </HStack></Td>
              </Tr>
            );
          })}
        </Tbody></Table></Box>
      </Card>
      <Modal isOpen={isOpen} onClose={onClose} size="lg"><ModalOverlay /><ModalContent>
        <ModalHeader>{editMode ? 'Editar evento' : 'Nuevo evento'}</ModalHeader><ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl mb={3} isRequired><FormLabel>Título</FormLabel><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></FormControl>
          <FormControl mb={3}><FormLabel>Tipo</FormLabel><Select value={form.eventType} onChange={e => setForm({ ...form, eventType: e.target.value })}>
            {Object.entries(TYPE_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </Select></FormControl>
          <Flex gap={3} mb={3}><FormControl isRequired><FormLabel>Inicio</FormLabel><Input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} /></FormControl>
            <FormControl><FormLabel>Fin</FormLabel><Input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} /></FormControl></Flex>
          <FormControl><FormLabel>Descripción</FormLabel><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></FormControl>
        </ModalBody>
        <ModalFooter><Button mr={3} onClick={onClose}>Cancelar</Button><Button colorScheme="brand" onClick={handleSave}>{editMode ? 'Actualizar' : 'Crear'}</Button></ModalFooter>
      </ModalContent></Modal>
    </Box>
  );
}
